package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Announcement
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.ReadReceipts
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilitiesAt as userCapabilitiesAt
import com.evoapps.evoshift.domain.model.feedOrder
import com.evoapps.evoshift.domain.repository.AnnouncementRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.team.MarkAnnouncementsReadUseCase
import com.evoapps.evoshift.domain.usecase.team.PostAnnouncementUseCase
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.announcement_deleted
import evoshift.composeapp.generated.resources.announcement_posted
import evoshift.composeapp.generated.resources.error_save_failed
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** The managers' read receipts of one post: loading until [receipts] arrive. */
data class ReceiptsView(val announcement: Announcement, val receipts: ReadReceipts? = null)

data class AnnouncementsUiState(
    /** Only until the first posts arrive. */
    val isLoading: Boolean = true,
    val workplaceId: String? = null,
    val userId: String = "",
    /** What the person may do here: posting and receipts need [Gate.ANNOUNCEMENTS_POSTING] / [Gate.READ_RECEIPTS]. */
    val capabilities: Set<Capability> = emptySet(),
    /** The posts for this person (managers: all of them), pinned first, then the newest. */
    val announcements: List<Announcement> = emptyList(),
    /** Posts the person had not read before they came on screen ("new"). */
    val unread: Set<String> = emptySet(),
    /** The workplace's roles, for posting to one of them. */
    val roles: List<String> = emptyList(),
    val composerOpen: Boolean = false,
    val isPosting: Boolean = false,
    val receipts: ReceiptsView? = null,
    /** A post waiting for "Delete?" to be confirmed. */
    val confirmDelete: Announcement? = null,
    val message: UiText? = null,
    val error: UiText? = null
)

/**
 * Team announcements (SH-118, from More and from the notification of a post). Everyone working at
 * the workplace reads the posts for them (a post to one role is listed for that role; managers
 * see all of them); opening the screen records read receipts and reads the matching
 * notifications. Managers post, pin, delete and see who read a post. Changes are optimistic.
 */
class AnnouncementsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val repository: AnnouncementRepository,
    private val postAnnouncement: PostAnnouncementUseCase,
    private val markRead: MarkAnnouncementsReadUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val getTeam: GetAssignableEmployeesUseCase,
    private val userRepository: UserRepository,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(AnnouncementsUiState())
    val uiState: StateFlow<AnnouncementsUiState> = _uiState.asStateFlow()

    private val postsJob = JobSlot()
    private val capabilitiesJob = JobSlot()
    private val receiptsJob = JobSlot()
    /** Posts already looked up for this person (read or marked): each is marked only once. */
    private val seen = mutableSetOf<String>()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                seen.clear()
                _uiState.update { AnnouncementsUiState(isLoading = workplaceId != null, workplaceId = workplaceId) }
                if (workplaceId == null) return@collectLatest
                capabilitiesJob.launch(viewModelScope) {
                    capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId)
                        .collect { capabilities -> _uiState.update { it.copy(capabilities = capabilities) } }
                }
                launch {
                    getWorkplace(workplaceId).getOrNull()?.let { workplace ->
                        _uiState.update { it.copy(roles = workplace.settings.availableRoles.filter { role -> role.isNotBlank() }) }
                    }
                }
                observePosts(workplaceId)
            }
        }
    }

    private fun observePosts(workplaceId: String) {
        postsJob.launch(viewModelScope) {
            combine(getCurrentUserUseCase(), repository.observe(workplaceId)) { user, posts -> user to posts }
                // Refused (no longer working there) or offline without a cache: say so, keep the screen.
                .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
                .collect { (user, posts) ->
                    if (user == null) return@collect
                    val manages = Capability.MANAGE_TEAM in user.userCapabilitiesAt(workplaceId)
                    val role = user.engagements[workplaceId]?.role
                    val visible = posts.filter { manages || it.isFor(role) || it.authorId == user.uid }.feedOrder()
                    _uiState.update {
                        it.copy(isLoading = false, userId = user.uid, announcements = visible)
                    }
                    // Receipts only for posts meant for the person (a manager sees the others too).
                    recordReads(workplaceId, user, visible.filter { it.isFor(role) })
                }
        }
    }

    /** Receipts for the posts not looked up yet; the ones not read before stay marked "new". */
    private suspend fun recordReads(workplaceId: String, user: User, visible: List<Announcement>) {
        val fresh = visible.filter { it.id !in seen && it.authorId != user.uid }
        if (fresh.isEmpty()) return
        seen += fresh.map { it.id }
        val alreadyRead = repository.readIds(workplaceId, fresh.map { it.id }, user.uid).getOrDefault(emptySet())
        val unread = fresh.map { it.id }.toSet() - alreadyRead
        if (unread.isNotEmpty()) _uiState.update { it.copy(unread = it.unread + unread) }
        markRead(workplaceId, user.uid, fresh, alreadyRead)
        readNotificationsOf(user.uid, unread)
    }

    /** The inbox notifications of posts now read (the bell's count follows). */
    private suspend fun readNotificationsOf(userId: String, announcementIds: Set<String>) {
        if (announcementIds.isEmpty()) return
        val inbox = runCatching { userRepository.observeNotifications(userId).first() }.getOrNull() ?: return
        val ids = inbox.filter {
            !it.isRead && it.id.isNotEmpty() &&
                it.data[NotificationData.KIND] == NotificationData.Kind.ANNOUNCEMENT &&
                it.data[NotificationData.ANNOUNCEMENT_ID] in announcementIds
        }.map { it.id }
        if (ids.isNotEmpty()) userRepository.markNotificationsRead(userId, ids)
    }

    fun openComposer() = _uiState.update { it.copy(composerOpen = true) }

    fun closeComposer() = _uiState.update { it.copy(composerOpen = false) }

    /** Posts [text] to everyone or to [targetRole]; the dialog closes once it is saved. */
    fun post(text: String, pinned: Boolean, targetRole: String?) {
        val workplaceId = _uiState.value.workplaceId ?: return
        if (_uiState.value.isPosting) return
        _uiState.update { it.copy(isPosting = true) }
        viewModelScope.launch {
            val author = getCurrentUserUseCase().first() ?: return@launch _uiState.update { it.copy(isPosting = false) }
            postAnnouncement(workplaceId, author, text, pinned, targetRole)
                .onSuccess { posted ->
                    seen += posted.id
                    _uiState.update {
                        it.copy(
                            isPosting = false, composerOpen = false,
                            message = UiText.Resource(Res.string.announcement_posted),
                            // Shown at once; the listener confirms it.
                            announcements = (it.announcements.filterNot { a -> a.id == posted.id } + posted).feedOrder()
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(isPosting = false, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
                }
        }
    }

    fun setPinned(announcement: Announcement, pinned: Boolean) {
        val workplaceId = _uiState.value.workplaceId ?: return
        val before = _uiState.value.announcements
        _uiState.update { state ->
            state.copy(announcements = state.announcements.map { if (it.id == announcement.id) it.copy(pinned = pinned) else it }.feedOrder())
        }
        viewModelScope.launch {
            repository.setPinned(workplaceId, announcement.id, pinned, _uiState.value.userId).onFailure { e -> rollBack(before, e) }
        }
    }

    fun askDelete(announcement: Announcement?) = _uiState.update { it.copy(confirmDelete = announcement) }

    fun delete(announcement: Announcement) {
        val workplaceId = _uiState.value.workplaceId ?: return
        val before = _uiState.value.announcements
        _uiState.update { state ->
            state.copy(
                confirmDelete = null,
                announcements = state.announcements.filterNot { it.id == announcement.id },
                message = UiText.Resource(Res.string.announcement_deleted)
            )
        }
        viewModelScope.launch {
            repository.delete(workplaceId, announcement.id).onFailure { e -> rollBack(before, e) }
        }
    }

    /** Who read [announcement] and who not yet (managers). */
    fun openReceipts(announcement: Announcement) {
        val workplaceId = _uiState.value.workplaceId ?: return
        _uiState.update { it.copy(receipts = ReceiptsView(announcement)) }
        receiptsJob.launch(viewModelScope) {
            val team = getTeam(workplaceId).getOrDefault(emptyList())
            repository.reads(workplaceId, announcement.id)
                .onSuccess { reads ->
                    _uiState.update {
                        if (it.receipts?.announcement?.id != announcement.id) it
                        else it.copy(receipts = ReceiptsView(announcement, ReadReceipts.of(announcement, team, reads)))
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(receipts = null, error = e.toUiText()) } }
        }
    }

    fun closeReceipts() = _uiState.update { it.copy(receipts = null) }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }

    private fun rollBack(before: List<Announcement>, e: Throwable) {
        _uiState.update { it.copy(announcements = before, message = null, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
    }
}
